// Shell-level "Compose Message" modal opened from the customer record.
//
// Why this lives in the shell rather than the customer page: the customer
// record's Message button must not navigate to the Messages page (the
// retailer should stay on the record). A modal here keeps the popup
// self-contained and lets other surfaces (e.g. the contacts page, the CRM
// pipeline) call the same flow if they want.
//
// Endpoint: POST /portal/customer-message
//   { retailerId, retailerName, customerId, customerName, subject, body,
//     attachmentIds?: string[] }
//
// Attachment plumbing comes from ./message-attachments (shared with the
// Messages page so every compose surface speaks the same upload contract).

import { ApiError } from "@tkos/api-client";

import { attachAutoGrow } from "./auto-grow";
import "./customer-compose-modal.css";

import type { PageContext } from "./types";
import {
  ATTACH_ACCEPT_ATTR,
  attachmentTrayHtml,
  uploadMessageAttachments,
  type MessageAttachment,
} from "./message-attachments";

interface OpenArgs {
  /** Customer this message is being composed to. The To field is pre-filled
   *  with this contact — strict, no search, no freetext. When the record has an
   *  optional second contact (couples / joint customers), both are shown and the
   *  send notifies both parties. */
  customer: { id: string; name: string; email?: string; secondaryName?: string; secondaryEmail?: string };
  /** Logged-in retailer (used for the send payload). */
  ownRetailerId: string;
  retailerName: string;
  /** Pre-fill subject when provided (e.g. "Re: <thread>" from a deep-link). */
  defaultSubject?: string;
  /** Called on successful send. Lets the caller refresh the in-page Messages
   *  card without us having to import or know about it. */
  onSent?: (sent: { messageId?: string | null }) => void;
}

interface SendResponse {
  ok?: boolean;
  message?: { id?: string };
  error?: string;
}

function escAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Open the Compose Message popup. Returns the dialog element so callers can
 *  detect close, but the modal also fully manages its own lifecycle (closes
 *  on send / cancel / backdrop / Escape). */
export function openCustomerComposeModal(args: OpenArgs, ctx: PageContext): void {
  // Reuse an existing dialog if the user double-clicks Message — re-pop the
  // same modal rather than stacking two.
  const existing = document.getElementById("cust-compose-modal");
  if (existing) existing.remove();

  const dialog = document.createElement("dialog");
  dialog.id = "cust-compose-modal";
  dialog.className = "cust-compose-modal";
  dialog.innerHTML = `
    <form class="cust-compose-form" data-role="form" method="dialog">
      <header class="cust-compose-head">
        <h2>Message ${escAttr(args.customer.name)}</h2>
        <button type="button" class="cust-compose-close" data-action="close" aria-label="Close">&times;</button>
      </header>
      <div class="cust-compose-body">
        <div class="cust-compose-thread" data-role="thread" hidden></div>
        <div class="cust-compose-field">
          <span class="cust-compose-lbl">To</span>
          <div class="cust-compose-to" aria-readonly="true">
            <span class="cust-compose-to-chip">${escAttr(args.customer.name)}</span>
            ${(args.customer.secondaryName || args.customer.secondaryEmail)
              ? `<span class="cust-compose-to-chip">${escAttr(args.customer.secondaryName || args.customer.secondaryEmail || "Second contact")}</span>`
              : ""}
          </div>
          ${(args.customer.secondaryName || args.customer.secondaryEmail)
            ? `<p class="cust-compose-both-note">Both contacts will be notified — they share one portal.</p>`
            : ""}
        </div>
        <label class="cust-compose-field">
          <span class="cust-compose-lbl">Message</span>
          <textarea required rows="5" maxlength="8000" data-role="body"
                    placeholder="Write your message…"></textarea>
        </label>
        <div class="cust-compose-attach-row" data-role="drop-zone">
          <button type="button" class="cust-compose-attach-btn"
                  data-action="attach" aria-label="Attach file">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round"
                 stroke-linejoin="round" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            <span>Attach files</span>
          </button>
          <span class="cust-compose-attach-hint">
            Drag &amp; drop or click — JPG, PNG, WebP, PDF · max 10 MB each
          </span>
          <input type="file" class="cust-compose-attach-input"
                 data-role="attach-input"
                 accept="${ATTACH_ACCEPT_ATTR}" multiple hidden />
        </div>
        <div class="cust-compose-tray" data-role="tray" hidden></div>
      </div>
      <footer class="cust-compose-foot">
        <button type="button" class="cust-compose-btn" data-action="cancel">Cancel</button>
        <button type="submit" class="cust-compose-btn cust-compose-btn--primary"
                data-role="send">Send</button>
      </footer>
    </form>
  `;

  document.body.appendChild(dialog);

  const bodyEl    = dialog.querySelector<HTMLTextAreaElement>("[data-role='body']")!;
  attachAutoGrow(bodyEl, 280);
  const sendBtn   = dialog.querySelector<HTMLButtonElement>("[data-role='send']")!;
  const dropZone  = dialog.querySelector<HTMLDivElement>("[data-role='drop-zone']")!;
  const attachBtn = dialog.querySelector<HTMLButtonElement>("[data-action='attach']")!;
  const attachInp = dialog.querySelector<HTMLInputElement>("[data-role='attach-input']")!;
  const tray      = dialog.querySelector<HTMLDivElement>("[data-role='tray']")!;
  const threadEl  = dialog.querySelector<HTMLDivElement>("[data-role='thread']")!;

  const attachments: MessageAttachment[] = [];

  // ── Existing conversation (find-or-create is automatic server-side: the
  //    thread is keyed on the customer's email, so we just load any history
  //    here and render it above the compose box — an inline mini messages
  //    page). ────────────────────────────────────────────────────────────
  interface ThreadMsg { id: string; text: string; isOut: boolean; ts: number }

  const toMs = (v: unknown): number => {
    if (typeof v === "number" && Number.isFinite(v)) return v < 1e12 ? v * 1000 : v;
    if (typeof v === "string") { const t = Date.parse(v); if (Number.isFinite(t)) return t; }
    return Date.now();
  };

  const renderThread = (msgs: ThreadMsg[]): void => {
    if (msgs.length === 0) { threadEl.hidden = true; threadEl.innerHTML = ""; return; }
    const visible = msgs.slice(-20);
    threadEl.hidden = false;
    threadEl.innerHTML = visible
      .map((m) => {
        const ts = new Date(m.ts).toLocaleString("en-GB", {
          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
        return `
          <div class="cust-compose-bubble-row ${m.isOut ? "is-out" : "is-in"}">
            <div class="cust-compose-bubble ${m.isOut ? "is-out" : "is-in"}">${escAttr(m.text)}</div>
            <div class="cust-compose-bubble-meta">${escAttr(ts)}</div>
          </div>`;
      })
      .join("");
    threadEl.scrollTop = threadEl.scrollHeight;
  };

  const loadThread = async (): Promise<void> => {
    try {
      const masked = /[•·●]/.test(args.customer.email ?? "");
      const emailQs = args.customer.email && !masked
        ? `&customerEmail=${encodeURIComponent(args.customer.email)}`
        : "";
      const resp = await ctx.api<{ messages?: unknown[] }>(
        `/portal/communications?customerId=${encodeURIComponent(args.customer.id)}${emailQs}`,
        { method: "GET" },
      );
      const msgs: ThreadMsg[] = Array.isArray(resp?.messages)
        ? resp.messages
            .map((raw): ThreadMsg | null => {
              if (!raw || typeof raw !== "object") return null;
              const r = raw as Record<string, unknown>;
              const text = String(r["text"] ?? r["body"] ?? "").trim();
              if (!text) return null;
              const ts = toMs(r["ts"] ?? r["createdAt"] ?? r["created_at"] ?? null);
              const dir = r["isOut"] ?? r["direction"] ?? r["from"] ?? "";
              const isOut = dir === true || dir === "me" || dir === "out"
                || dir === "outbound" || dir === "retailer-to-customer"
                || dir === "supplier-to-retailer";
              return { id: String(r["id"] ?? `m-${ts}`), text, isOut, ts };
            })
            .filter((m): m is ThreadMsg => m !== null)
            .sort((a, b) => a.ts - b.ts)
        : [];
      renderThread(msgs);
    } catch {
      // No thread / endpoint missing — fall back to a clean compose box.
      renderThread([]);
    }
  };

  const repaintTray = (): void => {
    const html = attachmentTrayHtml(attachments);
    tray.hidden = attachments.length === 0;
    tray.innerHTML = html;
  };

  const close = (): void => {
    if (dialog.open) dialog.close();
    dialog.remove();
  };

  // ── Close handlers ─────────────────────────────────────────────────────
  dialog.addEventListener("click", (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    if (t === dialog) { close(); return; }
    const action = t.closest<HTMLElement>("[data-action]");
    if (!action) return;
    if (action.dataset["action"] === "close" || action.dataset["action"] === "cancel") {
      close();
    }
  });
  dialog.addEventListener("cancel", (e) => {
    // Native Esc — let the dialog close, then strip the node.
    e.preventDefault();
    close();
  });

  // ── Attachment wiring ──────────────────────────────────────────────────
  attachBtn.addEventListener("click", () => attachInp.click());
  attachInp.addEventListener("change", () => {
    const files = Array.from(attachInp.files ?? []);
    attachInp.value = "";
    if (files.length === 0) return;
    void (async () => {
      try {
        const uploaded = await uploadMessageAttachments(files);
        attachments.push(...uploaded);
        repaintTray();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed.";
        ctx.showToast(msg, "error");
      }
    })();
  });

  // Drag-and-drop on the whole drop zone row + tray.
  const stop = (e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); };
  for (const target of [dropZone, tray]) {
    (["dragenter", "dragover"] as const).forEach((evt) => {
      target.addEventListener(evt, (e) => {
        stop(e as DragEvent);
        target.classList.add("is-dragover");
      });
    });
    (["dragleave", "dragend"] as const).forEach((evt) => {
      target.addEventListener(evt, (e) => {
        stop(e as DragEvent);
        target.classList.remove("is-dragover");
      });
    });
    target.addEventListener("drop", (e) => {
      stop(e);
      target.classList.remove("is-dragover");
      const list = e.dataTransfer?.files;
      if (!list || list.length === 0) return;
      void (async () => {
        try {
          const uploaded = await uploadMessageAttachments(Array.from(list));
          attachments.push(...uploaded);
          repaintTray();
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Upload failed.";
          ctx.showToast(msg, "error");
        }
      })();
    });
  }

  // Remove queued attachment.
  tray.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
      "[data-role='attach-remove']",
    );
    if (!btn) return;
    const id = btn.dataset["attachId"];
    if (!id) return;
    const idx = attachments.findIndex((a) => a.id === id);
    if (idx >= 0) attachments.splice(idx, 1);
    repaintTray();
  });

  // ── Submit ─────────────────────────────────────────────────────────────
  const form = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    // Customer messages have no subject line — the server defaults one. Keep
    // any caller-supplied default (e.g. a reply context) for the record.
    const subject = (args.defaultSubject ?? "").trim();
    const body    = bodyEl.value.trim();
    if (!body && attachments.length === 0) { bodyEl.focus(); return; }
    sendBtn.disabled = true;
    void (async () => {
      try {
        const resp = await ctx.api<SendResponse>("/portal/customer-message", {
          method: "POST",
          body: JSON.stringify({
            retailerId: args.ownRetailerId,
            retailerName: args.retailerName,
            customerId: args.customer.id,
            customerName: args.customer.name,
            // Real (unmasked) email — the server uses this as the customer
            // participant key so retailer→customer messages land in the
            // customer portal inbox (keyed on email, not the CRM id).
            customerEmail: args.customer.email ?? "",
            subject,
            body,
            attachmentIds: attachments.map((a) => a.id),
          }),
        });
        ctx.showToast("Message sent", "success");
        args.onSent?.({ messageId: resp?.message?.id ?? null });
        // Stay inline (mini messages page): clear the compose box, drop any
        // queued attachments, and refresh the thread so the just-sent message
        // appears in the conversation above.
        bodyEl.value = "";
        attachments.length = 0;
        repaintTray();
        sendBtn.disabled = false;
        void loadThread();
        bodyEl.focus();
      } catch (err) {
        const msg = err instanceof ApiError
          ? `Send failed (HTTP ${err.status})`
          : err instanceof Error ? err.message : "Send failed";
        ctx.showToast(msg, "error");
        sendBtn.disabled = false;
      }
    })();
  });

  // ── Open ───────────────────────────────────────────────────────────────
  if (typeof dialog.showModal === "function") {
    dialog.showModal();
  } else {
    dialog.setAttribute("open", "");
  }
  void loadThread();
  setTimeout(() => bodyEl.focus(), 50);
}
